/**
 * LiveSessionDetail — pre-join lobby for a scheduled session. Shows meta,
 * countdown, host, features, and a big Join button. Also lets the host
 * edit or cancel.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import {
  Video, Calendar, Clock, Users, ArrowLeft, ShieldCheck, X, PenSquare,
  Copy, CheckCircle2, Trash2,
} from 'lucide-react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';

const featureChips = (features = {}) => {
  const items = [
    ['chat',        'Chat'],
    ['screenshare', 'Screen share'],
    ['whiteboard',  'Whiteboard'],
    ['breakouts',   'Breakout rooms'],
    ['hand_raise',  'Hand raise'],
    ['polls',       'Polls'],
  ];
  return items.filter(([k]) => features[k]);
};

const LiveSessionDetail = () => {
  const { API, user } = useContext(AuthContext);
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/live-sessions/${sessionId}`);
      setSession(data);
    } catch (err) {
      toast.error('Session not found');
      navigate('/live');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sessionId]);

  const join = async () => {
    try {
      setJoining(true);
      // Server-side attendance is created inside the room too, but pinging
      // it here surfaces gating errors before Jitsi mounts.
      await axios.post(`${API}/live-sessions/${sessionId}/join`);
      navigate(`/live/${sessionId}/room`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not join');
    } finally { setJoining(false); }
  };

  const cancelSession = async () => {
    if (!window.confirm('Cancel this session? Attendees will be notified.')) return;
    try {
      await axios.delete(`${API}/live-sessions/${sessionId}`);
      toast.success('Session cancelled');
      navigate('/live');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Could not copy'));
  };

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-background">
        <ToolSelector />
        <div className="flex items-center justify-center text-muted-foreground py-16">Loading session…</div>
      </div>
    );
  }

  const start = parseISO(session.scheduled_start);
  const status = session.status;
  const isLive = status === 'live';
  const isEnded = status === 'ended' || status === 'cancelled';
  const canJoin = !isEnded;
  const chips = featureChips(session.features);

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <Link to="/live" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-live">
          <ArrowLeft className="w-3.5 h-3.5" /> Live sessions
        </Link>

        <div className="pro-card p-6" data-testid="live-session-detail">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
              <Video className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {isLive && (
                  <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-rose-500 text-white">
                    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> LIVE NOW
                  </span>
                )}
                {status === 'scheduled' && (
                  <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-primary/15 text-primary">
                    <Calendar className="w-3 h-3" /> Starts {formatDistanceToNow(start, { addSuffix: true })}
                  </span>
                )}
                {isEnded && (
                  <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-muted text-muted-foreground">
                    {status === 'cancelled' ? <X className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />} {status}
                  </span>
                )}
                {session.is_host && (
                  <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500/15 text-emerald-700">
                    <ShieldCheck className="w-3 h-3" /> You host
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground mt-2 leading-tight">
                {session.title}
              </h1>
              {session.description && (
                <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{session.description}</p>
              )}
            </div>
          </div>

          {/* Meta grid */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetaTile icon={<Calendar className="w-3 h-3" />} label="Starts" value={format(start, 'EEE MMM d, h:mm a')} />
            <MetaTile icon={<Clock className="w-3 h-3" />}    label="Duration" value={`${session.duration_minutes} min`} />
            <MetaTile icon={<Users className="w-3 h-3" />}    label="In room now" value={session.live_attendee_count || 0} />
            <MetaTile icon={<ShieldCheck className="w-3 h-3" />} label="Host" value={session.host_name} />
          </div>

          {/* Features */}
          {chips.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Included</div>
              <div className="flex flex-wrap gap-1.5">
                {chips.map(([, label]) => (
                  <span key={label} className="text-[11px] font-bold rounded-full border border-border bg-muted/50 px-2 py-0.5">{label}</span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {canJoin && (
              <Button
                size="lg"
                onClick={join}
                disabled={joining}
                data-testid="join-session-btn"
                className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {joining ? 'Joining…' : (isLive ? 'Join live session' : 'Join lobby')}
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              onClick={copyLink}
              className="rounded-full font-bold"
              data-testid="detail-copy-link-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copy share link
            </Button>
            {session.is_host && !isEnded && (
              <>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => toast.info('Inline edit coming soon — for now, cancel and reschedule.')}
                  className="rounded-full font-bold ml-auto"
                  data-testid="detail-edit-btn"
                >
                  <PenSquare className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={cancelSession}
                  className="rounded-full font-bold text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                  data-testid="detail-cancel-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground text-center">
          Powered by Jitsi Meet — camera, screen share, whiteboard, breakout rooms and polls all work in-browser.
        </div>
      </div>

      {/* Recordings list */}
      <RecordingsList sessionId={session.id} isHost={!!session.is_host} />
    </div>
  );
};

const RecordingsList = ({ sessionId, isHost }) => {
  const { API } = React.useContext(AuthContext);
  const [recs, setRecs] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [openRec, setOpenRec] = React.useState(null);
  const videoRefs = React.useRef({});

  const load = React.useCallback(() => {
    axios.get(`${API}/live-sessions/${sessionId}/recordings`)
      .then((r) => setRecs(r.data?.recordings || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [API, sessionId]);
  React.useEffect(() => { load(); }, [load]);

  // Poll while any recording is still processing.
  React.useEffect(() => {
    const anyProcessing = recs.some((r) => ['pending', 'processing'].includes(r.transcript_status));
    if (!anyProcessing) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [recs, load]);

  const del = async (id) => {
    if (!window.confirm('Delete this recording?')) return;
    try {
      await axios.delete(`${API}/live-sessions/${sessionId}/recordings/${id}`);
      toast.success('Recording deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const retry = async (id) => {
    try {
      await axios.post(`${API}/live-sessions/${sessionId}/recordings/${id}/retranscribe`);
      toast.success('Transcription retry queued');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const seekTo = (recId, seconds) => {
    const v = videoRefs.current[recId];
    if (v) { try { v.currentTime = Math.max(0, seconds); v.play?.(); } catch { /* ignore */ } }
  };

  if (!loaded || recs.length === 0) return null;

  const formatDuration = (s) => {
    if (!s) return '';
    const m = Math.floor(s / 60), sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };
  const formatStamp = (s) => {
    const total = Math.max(0, Math.floor(s));
    const m = Math.floor(total / 60), sec = total % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pb-8">
      <div className="pro-card p-6" data-testid="session-recordings">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-extrabold">Recordings · {recs.length}</h2>
        </div>
        <ul className="space-y-4">
          {recs.map((r) => {
            const isOpen = openRec === r.id;
            const status = r.transcript_status || 'pending';
            return (
              <li key={r.id} className="rounded-lg border border-border p-3" data-testid={`recording-${r.id}`}>
                <div className="flex items-start gap-3">
                  <video
                    ref={(el) => { videoRefs.current[r.id] = el; }}
                    src={r.file_url}
                    controls
                    preload="metadata"
                    className="w-40 aspect-video rounded-md bg-black flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{r.title || 'Untitled recording'}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {formatDuration(r.duration_sec)}
                      {r.size_bytes ? ` · ${(r.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                      {r.created_at ? (() => { try { return ` · ${formatDistanceToNow(parseISO(r.created_at), { addSuffix: true })}`; } catch { return ''; } })() : ''}
                    </div>
                    {/* Status pill */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {status === 'ready' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold" data-testid={`transcript-status-${r.id}`}>
                          ✓ Transcript ready
                        </span>
                      )}
                      {status === 'processing' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold animate-pulse" data-testid={`transcript-status-${r.id}`}>
                          ⏳ Transcribing…
                        </span>
                      )}
                      {status === 'pending' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold" data-testid={`transcript-status-${r.id}`}>
                          Queued for transcription
                        </span>
                      )}
                      {status === 'failed' && (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-400 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold" data-testid={`transcript-status-${r.id}`}>
                            Transcription failed
                          </span>
                          {(isHost) && (
                            <button
                              type="button"
                              onClick={() => retry(r.id)}
                              className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
                              data-testid={`transcript-retry-${r.id}`}
                            >
                              Retry
                            </button>
                          )}
                        </>
                      )}
                      {status === 'ready' && (r.chapters?.length > 0 || r.transcript) && (
                        <button
                          type="button"
                          onClick={() => setOpenRec(isOpen ? null : r.id)}
                          className="text-[11px] font-bold text-primary hover:underline"
                          data-testid={`transcript-toggle-${r.id}`}
                        >
                          {isOpen ? 'Hide transcript' : `${r.chapters?.length || 0} chapter${(r.chapters?.length || 0) === 1 ? '' : 's'} · view transcript`}
                        </button>
                      )}
                    </div>
                  </div>
                  <a
                    href={r.file_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs font-bold text-primary hover:underline whitespace-nowrap"
                    data-testid={`recording-download-${r.id}`}
                  >
                    Download ↗
                  </a>
                  {isHost && (
                    <button
                      type="button"
                      onClick={() => del(r.id)}
                      className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-destructive whitespace-nowrap"
                      data-testid={`recording-delete-${r.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>

                {/* Chapters + Transcript panel */}
                {isOpen && status === 'ready' && (
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-3">
                    {r.chapters?.length > 0 && (
                      <div data-testid={`transcript-chapters-${r.id}`}>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Chapters</div>
                        <ol className="space-y-1.5">
                          {r.chapters.map((ch, i) => (
                            <li key={i}>
                              <button
                                type="button"
                                onClick={() => seekTo(r.id, ch.start_sec)}
                                className="text-left w-full rounded-lg p-2 hover:bg-muted transition-colors"
                                data-testid={`transcript-chapter-${r.id}-${i}`}
                              >
                                <div className="text-[10px] font-bold text-primary tabular-nums">
                                  {formatStamp(ch.start_sec)}
                                </div>
                                <div className="text-xs font-semibold text-foreground leading-tight">{ch.title}</div>
                                {ch.summary && (
                                  <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{ch.summary}</div>
                                )}
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Transcript</div>
                      <div className="max-h-96 overflow-y-auto text-sm text-foreground whitespace-pre-wrap leading-relaxed rounded-lg border border-border p-3 bg-muted/30" data-testid={`transcript-text-${r.id}`}>
                        {r.transcript || <span className="italic text-muted-foreground">No transcript text.</span>}
                      </div>
                      {/* Ask this recording */}
                      <AskRecording
                        sessionId={sessionId}
                        recordingId={r.id}
                        onSeek={(s) => seekTo(r.id, s)}
                      />
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const MetaTile = ({ icon, label, value }) => (
  <div className="rounded-xl border border-border bg-muted/30 p-3">
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="text-sm font-extrabold text-foreground truncate">{value}</div>
  </div>
);

// ---------------------------------------------------------------------------
// AskRecording — inline AI Q&A ("Ask this recording"). Grounded on the
// transcript segments, returns an answer + [m:ss] citations that seek the
// inline video when clicked.
// ---------------------------------------------------------------------------
const AskRecording = ({ sessionId, recordingId, onSeek }) => {
  const { API } = React.useContext(AuthContext);
  const [question, setQuestion] = React.useState('');
  const [asking, setAsking] = React.useState(false);
  const [result, setResult] = React.useState(null);   // { answer, citations }

  const ask = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/live-sessions/${sessionId}/recordings/${recordingId}/ask`,
        { question: q },
      );
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ask failed');
    } finally { setAsking(false); }
  };

  const formatStamp = (s) => {
    const t = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, '0')}`;
  };

  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid={`ask-recording-${recordingId}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1.5 inline-flex items-center gap-1">
        ✨ Ask this recording
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !asking) ask(); }}
          placeholder="e.g. what did they say about tray loading?"
          className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
          data-testid={`ask-input-${recordingId}`}
        />
        <button
          type="button"
          onClick={ask}
          disabled={asking || !question.trim()}
          className="h-9 px-3.5 rounded-md bg-primary text-primary-foreground text-xs font-bold disabled:opacity-50 whitespace-nowrap"
          data-testid={`ask-submit-${recordingId}`}
        >
          {asking ? 'Thinking…' : 'Ask'}
        </button>
      </div>
      {result && (
        <div className="mt-3 space-y-2 text-sm" data-testid={`ask-answer-${recordingId}`}>
          <div className="rounded-md bg-card p-3 border border-border leading-relaxed">
            {result.answer}
          </div>
          {result.citations?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Cited moments</div>
              <ul className="space-y-1">
                {result.citations.map((c, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => onSeek && onSeek(c.start_sec)}
                      className="w-full text-left rounded-md hover:bg-muted p-2 -mx-2 flex items-start gap-2 transition-colors"
                      data-testid={`ask-citation-${recordingId}-${i}`}
                    >
                      <span className="text-[11px] font-bold text-primary tabular-nums flex-shrink-0 mt-0.5">
                        {formatStamp(c.start_sec)}
                      </span>
                      <span className="text-xs text-foreground italic">
                        &ldquo;{c.quote}&rdquo;
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default LiveSessionDetail;
